import { useState, useEffect, memo } from 'react';
import { Building2, MapPin, Phone, Mail, Globe, User, CreditCard, FileText } from '../../icons/wp-icons';

// InputField MUSS außerhalb der Hauptkomponente definiert werden
const InputField = memo(function InputField({
  icon: Icon,
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required = false,
  half = false
}) {
  return (
    <div className={half ? 'col-span-1' : 'col-span-2'}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        )}
        <input
          type={type}
          value={value}
          onChange={onChange}
          className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2] focus:border-transparent`}
          placeholder={placeholder}
        />
      </div>
    </div>
  );
});

function FirmendatenStep({ onDataChange, onboardingData, user }) {
  const existingData = onboardingData?.firmendaten || {};

  const [formData, setFormData] = useState({
    firmenname: existingData.firmenname || '',
    strasse: existingData.strasse || '',
    plz: existingData.plz || '',
    ort: existingData.ort || '',
    telefon: existingData.telefon || '',
    email: existingData.email || user?.email || '',
    website: existingData.website || '',
    geschaeftsfuehrer: existingData.geschaeftsfuehrer || user?.name || '',
    geschaeftsfuehrerTitel: existingData.geschaeftsfuehrerTitel || '',
    iban: existingData.iban || '',
    bic: existingData.bic || '',
    bankname: existingData.bankName || '',
    ustId: existingData.ustIdNr || '',
    steuernummer: existingData.steuernummer || '',
    handwerkskammer: existingData.handwerkskammer || ''
  });

  // Beim Mount und bei Änderungen die Daten an Parent übergeben
  useEffect(() => {
    const isValid = formData.firmenname.trim() && formData.ort.trim();
    onDataChange(formData, isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Ihre Firmendaten
        </h3>
        <p className="text-gray-600 text-sm mb-6">
          Diese Angaben erscheinen auf Ihren Rechnungen, Angeboten und in der Kundenkommunikation.
        </p>
      </div>

      {/* Hauptdaten */}
      <div className="grid grid-cols-2 gap-4">
        <InputField
          icon={Building2}
          label="Firmenname"
          value={formData.firmenname}
          onChange={handleChange('firmenname')}
          placeholder="Ihre Firma GmbH"
          required
        />

        <InputField
          icon={MapPin}
          label="Straße & Hausnummer"
          value={formData.strasse}
          onChange={handleChange('strasse')}
          placeholder="Musterstraße 123"
        />

        <InputField
          label="PLZ"
          value={formData.plz}
          onChange={handleChange('plz')}
          placeholder="12345"
          half
        />

        <InputField
          label="Ort"
          value={formData.ort}
          onChange={handleChange('ort')}
          placeholder="Musterstadt"
          required
          half
        />

        <InputField
          icon={Phone}
          label="Telefon"
          value={formData.telefon}
          onChange={handleChange('telefon')}
          type="tel"
          placeholder="+49 123 456789"
        />

        <InputField
          icon={Mail}
          label="E-Mail"
          value={formData.email}
          onChange={handleChange('email')}
          type="email"
          placeholder="info@firma.de"
        />

        <InputField
          icon={Globe}
          label="Website"
          value={formData.website}
          onChange={handleChange('website')}
          placeholder="www.firma.de"
        />
      </div>

      {/* Geschäftsführer */}
      <div className="border-t pt-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Geschäftsführung</h4>
        <div className="grid grid-cols-2 gap-4">
          <InputField
            icon={User}
            label="Name"
            value={formData.geschaeftsfuehrer}
            onChange={handleChange('geschaeftsfuehrer')}
            placeholder="Max Mustermann"
          />

          <InputField
            label="Titel (optional)"
            value={formData.geschaeftsfuehrerTitel}
            onChange={handleChange('geschaeftsfuehrerTitel')}
            placeholder="z.B. Glasermeister"
          />
        </div>
      </div>

      {/* Bankdaten */}
      <div className="border-t pt-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Bankverbindung</h4>
        <div className="grid grid-cols-2 gap-4">
          <InputField
            icon={CreditCard}
            label="IBAN"
            value={formData.iban}
            onChange={handleChange('iban')}
            placeholder="DE89 3704 0044 0532 0130 00"
          />

          <InputField
            label="BIC"
            value={formData.bic}
            onChange={handleChange('bic')}
            placeholder="COBADEFFXXX"
            half
          />

          <InputField
            label="Bank"
            value={formData.bankname}
            onChange={handleChange('bankname')}
            placeholder="Sparkasse Musterstadt"
            half
          />
        </div>
      </div>

      {/* Steuerdaten */}
      <div className="border-t pt-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Steuerdaten</h4>
        <div className="grid grid-cols-2 gap-4">
          <InputField
            icon={FileText}
            label="USt-IdNr."
            value={formData.ustId}
            onChange={handleChange('ustId')}
            placeholder="DE123456789"
            half
          />

          <InputField
            label="Steuernummer"
            value={formData.steuernummer}
            onChange={handleChange('steuernummer')}
            placeholder="123/456/78901"
            half
          />

          <InputField
            label="Handwerkskammer"
            value={formData.handwerkskammer}
            onChange={handleChange('handwerkskammer')}
            placeholder="z.B. HWK München"
          />
        </div>
      </div>

      <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800">
        <strong>Hinweis:</strong> Sie können diese Daten später jederzeit in den Einstellungen ändern.
      </div>
    </div>
  );
}

export default FirmendatenStep;
